import { AuthContext, camelize } from '@repo-level/core-front';
import type {
  Jackpot,
  JackpotWin,
  Lootbox,
  PlayerBonus,
  PlayerFreespin,
  PlayerTransaction,
  ReferralProgramRealTime,
} from 'api/common';
import {
  jackpots,
  jackpotWinsList,
  playerBonuses,
  playerFreespins,
  playerLootboxes,
} from 'api/common';
import { groupMemberActiveBonus } from 'helpers/user';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import type { State } from './reducer';
import { ACTION_TYPE, reducer } from './reducer';

const MINUTE_IN_MILLISECONDS = 60_000;

export type WSResponse<R> = {
  uid: string;
  channel: string;
  data: R;
};

interface RealTimeServiceContextData extends State {
  fetchLootboxes: () => void;
  fetchBonuses: () => void;
}

export const RealTimeServiceContext = createContext<RealTimeServiceContextData>(
  null as any
);

const initialState: State = {
  lootboxes: [],
  jackpots: [],
  jackpotWins: [],
  jackpotWinsList: [],
  activeJackpot: undefined,
  playerPayments: [],
  bonuses: [],
  bonusesPending: true,
  freespins: [],
  freespinsPending: true,
  referralProgram: null,
};

export const RealTimeServiceContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { isAuthenticated, user, playerSettings } = useContext(AuthContext);

  const fetchLootboxes = useCallback(async () => {
    try {
      const { data: lootboxes } = await playerLootboxes();

      dispatch({
        type: ACTION_TYPE.SET_LOOTBOXES,
        payload: { lootboxes },
      });
    } catch {}
  }, []);

  useEffect(() => {
    if (user?.id == null) return;

    (async () => {
      try {
        await fetchLootboxes();
      } catch {}
    })();
  }, [user?.id, fetchLootboxes]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: jackpotsData }, { data: jackpotWins }] =
          await Promise.all([jackpots(), jackpotWinsList()]);

        dispatch({
          type: ACTION_TYPE.SET_JACKPOTS,
          payload: { jackpots: jackpotsData },
        });

        dispatch({
          type: ACTION_TYPE.SET_JACKPOT_WINS_LIST,
          payload: { jackpotWinsList: jackpotWins },
        });
      } catch {}
    })();
  }, []);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_ACTIVE_JACKPOT,
    });
  }, [state.jackpots]);

  useEffect(() => {
    if (!playerSettings) return;

    // @ts-ignore
    const centrifuge = new Centrifuge({
      url: playerSettings.cent.url + '/connection/websocket',
      user: playerSettings.cent.user,
      timestamp: playerSettings.cent.timestamp,
      token: playerSettings.cent.token,
    });

    centrifuge.subscribe(
      'public:jackpots_changes',
      (context: WSResponse<Jackpot>) => {
        dispatch({
          type: ACTION_TYPE.UPDATE_JACKPOT,
          payload: { jackpot: camelize(context.data) as Jackpot },
        });
      }
    );

    if (playerSettings?.cent.user) {
      centrifuge.subscribe(
        `bonuses_changes#${playerSettings.cent.user}`,
        (context: WSResponse<PlayerBonus>) => {
          dispatch({
            type: ACTION_TYPE.UPDATE_BONUSES,
            payload: { bonus: camelize(context.data) as PlayerBonus },
          });
        }
      );

      centrifuge.subscribe(
        `freespins_changes#${playerSettings.cent.user}`,
        (context: WSResponse<PlayerFreespin>) => {
          dispatch({
            type: ACTION_TYPE.UPDATE_FREESPINS,
            payload: { freespin: camelize(context.data) as PlayerFreespin },
          });
        }
      );

      centrifuge.subscribe(
        `lootboxes_changes#${playerSettings.cent.user}`,
        (context: WSResponse<Lootbox>) => {
          dispatch({
            type: ACTION_TYPE.ADD_LOOTBOX,
            payload: { lootbox: camelize(context.data) as Lootbox },
          });
        }
      );

      centrifuge.subscribe(
        'jackpot_win_award',
        (context: WSResponse<JackpotWin>) => {
          dispatch({
            type: ACTION_TYPE.ADD_JACKPOT_WIN,
            payload: { jackpotWin: camelize(context.data) as JackpotWin },
          });
        }
      );

      centrifuge.subscribe(
        `payments_changes#${playerSettings.cent.user}`,
        (context: WSResponse<PlayerTransaction>) => {
          dispatch({
            type: ACTION_TYPE.ADD_PAYMENT,
            payload: { payment: camelize(context.data) as PlayerTransaction },
          });
        }
      );
    }

    centrifuge.subscribe(
      `referral_system_notifications#${user?.id}`,
      (context: WSResponse<ReferralProgramRealTime>) => {
        dispatch({
          type: ACTION_TYPE.REFERRAL_PROGRAM,
          payload: {
            referralProgram: camelize(context.data) as ReferralProgramRealTime,
          },
        });
      }
    );

    centrifuge.connect();

    return () => {
      centrifuge.disconnect();
    };
  }, [playerSettings, user]);

  const fetchBonuses = useCallback(async () => {
    try {
      const [{ data: bonuses }, { data: freespins }] = await Promise.all([
        playerBonuses(),
        playerFreespins(),
      ]);

      dispatch({
        type: ACTION_TYPE.SET_FREESPINS,
        payload: { freespins },
      });

      dispatch({
        type: ACTION_TYPE.SET_BONUSES,
        payload: { bonuses },
      });
    } catch {}
  }, []);

  useEffect(() => {
    if (user?.id == null) return;
    fetchBonuses();
  }, [user?.id, fetchLootboxes, fetchBonuses]);

  useEffect(() => {
    if (user?.id == null || !groupMemberActiveBonus(user)) return;

    const fetchBonuses = async () => {
      try {
        const { data: bonuses } = await playerBonuses();

        dispatch({
          type: ACTION_TYPE.SET_BONUSES,
          payload: { bonuses },
        });
      } catch {}
    };

    const fetchBonusesInterval = setInterval(
      () => fetchBonuses(),
      MINUTE_IN_MILLISECONDS
    );

    return () => clearInterval(fetchBonusesInterval);
  }, [user]);

  return (
    <RealTimeServiceContext.Provider
      value={{
        ...state,
        bonuses: isAuthenticated ? state.bonuses : [],
        fetchLootboxes,
        fetchBonuses,
      }}
    >
      {children}
    </RealTimeServiceContext.Provider>
  );
};
